import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import "../../assets/css/dashboard.css"; // Ensure your CSS file is correctly imported
import UserSummary from "../../components/dashboardTabs/userSummary/UserSummary";
import Occasions from "../../components/dashboardTabs/occasions/Occasions";
import { useGlobalContext } from "../../context/GlobalContext";
import Experience from "../../components/dashboardTabs/experience/Experience";
import BrandScorecard from "../../components/dashboardTabs/brandScorecard/BrandScorecard";
import Frequency from "../../components/dashboardTabs/frequency/Frequency";
import Amount from "../../components/dashboardTabs/amount/Amount";
import Needs from "../../components/dashboardTabs/needs/Needs";
import CompareBy from "../../components/compareBy/CompareBy";
import Loyalty from "../../components/dashboardTabs/loyalty/Loyalty";
import {
  fetchEndingDateValue,
  getBrandsFormsAndSize,
  getFilterOptionsForCoaster,
  getHouseHoldData,
  getOccasions,
  getProductType,
  getSegmentsList,
} from "../../services/filterOptions/filter";
import handleError from "../../utils/handleError";
import {
  APP_CONSTANT,
  CLEAR_DASHBOARD_STATES,
  getAppConstantCategoryValue,
  getDefaultColumns,
  SAVE_BRANDS,
  SAVE_CUSTOM_RECRUIT_CATEGORY,
  SAVE_END_DATE,
  SAVE_FAVORITE_BRANDS,
  SAVE_FORMS,
  SAVE_HOUSEHOLD,
  SAVE_OCCASION,
  SAVE_PARTICIPANT_SEGMENT,
} from "../../utils/constants";
import OccasionsAndNeeds from "../../components/dashboardTabs/occassionsAndNeeds/OccassionsAndNeeds";
import UsageHeatmap from "../../components/dashboardTabs/customCharts/UsageHeatmap";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [apply, setApply] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    deviceCode,
    product_id,
    project_id,
    mappedCategory,
    category,
    isDemoCategory,
    isCustomRecruitCategory,
    isDualCategory,
  } = useGlobalContext();
  const { token, selectedClientName, user } = useSelector(
    (state) => state.auth,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [compareBy, setCompareBy] = useState({});
  const [checkedCRCategories, setCheckedCRCategories] = useState([]);
  const [selectedComparitionBy, setSelectedComparisonBy] = useState("brand");
  const [isCompareByClicked, setIsCompareByClicked] = useState(false);
  const [chooseComparison, setChooseComparison] = useState("brand");
  const [productType, setProductType] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [isFilterByApplied, setIsFilterByApplied] = useState(false);
  const containerRef = useRef(null);
  const stickyRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  const compareByDefaultVal = (
    isCustomRecruitCategory = false,
    favoriteBrands = [],
  ) => {
    const defaultColumns = getDefaultColumns(isCustomRecruitCategory);
    const resetData = defaultColumns.reduce((result, col, index) => {
      const key = `key_${index + 1}`;
      result[key] = {
        name:
          index === 0
            ? getAppConstantCategoryValue(isCustomRecruitCategory)
            : index === 1 &&
                isCustomRecruitCategory &&
                favoriteBrands?.length > 0
              ? favoriteBrands?.length === 1
                ? favoriteBrands?.join("")
                : `${selectedClientName} Brands`
              : col.value,
        value:
          index === 0
            ? getAppConstantCategoryValue(isCustomRecruitCategory)
            : index === 1 &&
                isCustomRecruitCategory &&
                favoriteBrands?.length > 0
              ? favoriteBrands?.join("|")
              : col.selectedComparisons.join("|") || "",
      };
      return result;
    }, {});

    return resetData;
  };

  // useEffect(() => {
  //   setCompareBy(compareByDefaultVal(isCustomRecruitCategory));
  // }, [isCustomRecruitCategory]);

  const getEndingDate = async (payload, token, type) => {
    fetchEndingDateValue(payload, token, type)
      .then((data) => {
        dispatch({
          type: SAVE_END_DATE,
          payload: data?.data?.end_date || "",
        });
      })
      .catch((err) => {
        console.log("err in ending date", err);
      });
  };

  const fetchFilterOptionData = () => {
    setLoading(true);

    const requestBody = {
      product_id: parseInt(product_id),
      project_id: parseInt(project_id),
    };
    if (project_id && product_id) {
      getProductType(requestBody, token)
        .then(async (response) => {
          if (response.status === 200) {
            const productType =
              deviceCode === 1 ? "product" : response.data.type;
            const request_body = {
              product_id: parseInt(product_id),
              product_type: productType,
              project_id: parseInt(project_id),
              mapped_category: mappedCategory,
            };

            getEndingDate(
              {
                product_id: parseInt(product_id),
                project_id: parseInt(project_id),
                mapped_category: mappedCategory,
              },
              token,
              "sensor",
            );

            const [brandsFormsAndSizeResponse, occasionsResponse] =
              await Promise.all([
                getBrandsFormsAndSize(request_body, token),
                // getSegmentsList(request_body, token),
                getOccasions(request_body, token),
              ]);

            const favoriteBrands =
              brandsFormsAndSizeResponse?.data?.barcode_details
                ?.favorite_brand || [];
            let brandsData =
              brandsFormsAndSizeResponse?.data?.barcode_details?.brands || [];

            // handle show favorite brands on top as selected items
            if (favoriteBrands && favoriteBrands?.length > 0) {
              let unSelectedBrands = brandsData?.filter(
                (brandData) => !favoriteBrands?.includes(brandData),
              );
              let selectedBrands = brandsData?.filter((brandData) =>
                favoriteBrands?.includes(brandData),
              );

              brandsData = [...selectedBrands, ...unSelectedBrands];
            }
            dispatch({
              type: SAVE_BRANDS,
              payload: brandsData,
            });

            dispatch({
              type: SAVE_FAVORITE_BRANDS,
              payload: favoriteBrands,
            });

            dispatch({
              type: SAVE_CUSTOM_RECRUIT_CATEGORY,
              payload: brandsFormsAndSizeResponse?.data?.categories || {},
            });

            dispatch({
              type: SAVE_FORMS,
              payload:
                brandsFormsAndSizeResponse?.data?.barcode_details?.forms || [],
            });

            dispatch({
              type: SAVE_OCCASION,
              payload: occasionsResponse?.data?.occasions || {},
            });

            setCompareBy(
              compareByDefaultVal(isCustomRecruitCategory, favoriteBrands),
            );

            setProductType(productType);

            // dispatch( {
            //   type: SAVE_PARTICIPANT_SEGMENT,
            //   payload: segmentsListResponse?.data?.segments || []
            // });
          } else {
            console.log(
              "Failed to fetch product types. Unexpected status code.",
            );
          }
        })
        .catch((error) => {
          handleError(error, navigate, dispatch);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fetchFilterCompareOptionsForCoaster = async () => {
    try {
      setLoading(true);

      getEndingDate(
        {
          category_id: parseInt(product_id),
          study_id: parseInt(project_id),
          mapped_category: mappedCategory,
          category: category,
        },
        token,
        "coaster",
      );

      const [brandsResponse, formsResponse] = await Promise.all([
        getFilterOptionsForCoaster(project_id, category, "qs_brand", token),
        getFilterOptionsForCoaster(project_id, category, "form", token),
      ]);

      let brandResponseData = brandsResponse?.data?.data || [];
      const formResponseData = formsResponse?.data?.data || [];
      const favoriteBrands = brandsResponse?.data?.favorite_brand || [];

      // handle show favorite brands on top as selected items
      if (favoriteBrands && favoriteBrands?.length > 0) {
        let unSelectedBrands = brandResponseData?.filter(
          (brandData) => !favoriteBrands?.includes(brandData),
        );
        let selectedBrands = brandResponseData?.filter((brandData) =>
          favoriteBrands?.includes(brandData),
        );

        brandResponseData = [...selectedBrands, ...unSelectedBrands];
      }

      dispatch({
        type: SAVE_BRANDS,
        payload: brandResponseData,
      });

      dispatch({
        type: SAVE_FAVORITE_BRANDS,
        payload: favoriteBrands,
      });

      dispatch({
        type: SAVE_FORMS,
        payload: formResponseData,
      });
    } catch (error) {
      console.error("Error fetching filter compare options:", error);
    } finally {
      setLoading(false);
    }
  };

  const getHouseHoldDataOptions = () => {
    getHouseHoldData(parseInt(project_id), token)
      .then((response) => {
        if (response.status === 200) {
          dispatch({
            type: SAVE_HOUSEHOLD,
            payload: response?.data?.data || {},
          });
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      });
  };

  useEffect(() => {
    setActiveIndex(isCustomRecruitCategory ? 1 : 0);
    dispatch({
      type: CLEAR_DASHBOARD_STATES,
    }); // clear dashboard store states to empty when category item changed
    if (project_id) {
      getHouseHoldDataOptions();
      if (deviceCode === 1) {
        fetchFilterCompareOptionsForCoaster();
      } else {
        fetchFilterOptionData();
      }
    }

    // clearing filter by selection
    setSelectedFilters({});
    setIsFilterByApplied(false);
    setSelectedFilterValues({});
  }, [parseInt(product_id), isDemoCategory, isCustomRecruitCategory]);

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      const sticky = stickyRef.current;

      if (!container || !sticky) return;

      const containerRect = container.getBoundingClientRect();
      const stickyHeight = sticky.offsetHeight;

      // console.log("stickyHeight", stickyHeight);
      // console.log("container", container.offsetHeight);
      // console.log("containerRect.top", containerRect.top);
      // console.log("containerRect.bottom", containerRect.bottom);

      // Start sticky when sticky's top reaches the top of viewport
      const startSticky = containerRect.top <= 150;

      // Unstick when sticky's bottom reaches container's bottom
      const endSticky = containerRect.bottom <= stickyHeight;
      // console.log(endSticky)

      setIsSticky(startSticky && !endSticky);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="container-fluid page-wrapper">
      <div className="row">
        <div className="col-12 col-md-12">
          <Card className="shadow">
            <CompareBy
              compareBy={compareBy}
              setCompareBy={setCompareBy}
              setCheckedCRCategories={setCheckedCRCategories}
              setSelectedComparisonBy={setSelectedComparisonBy}
              apply={apply}
              setApply={setApply}
              loading={loading}
              setLoading={setLoading}
              isCompareByClicked={isCompareByClicked}
              setIsCompareByClicked={setIsCompareByClicked}
              chooseComparison={chooseComparison}
              setChooseComparison={setChooseComparison}
              tabActiveIndex={activeIndex}
              setProductType={setProductType}
              productType={productType}
            />
          </Card>
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-12 col-md-12" style={{ marginTop: "-10px" }}>
          <Card className="shadow right-panel-tabs">
            <div
              className={`${isSticky ? "sticky-container-wrapper" : ""} tab-view`}
              ref={containerRef}
              style={{ zIndex: "999" }}
            >
              <TabView
                className={`${isSticky ? "sticky-view" : ""}`}
                activeIndex={activeIndex}
                ref={stickyRef}
                onTabChange={(e) => {
                  setActiveIndex(e.index);
                  // Added reset Comparison and compareby values to show default view in chart and stop multiple api calls for now.
                  // Keep selected fields when move to brand scorecard and frequency will fix later.
                  // if (e.index === 0) {
                  //   setChooseComparison("");
                  //   setSelectedComparisonBy("");
                  //   setCompareBy(compareByDefaultVal);
                  // }
                }}
              >
                {!isCustomRecruitCategory ? (
                  <TabPanel header="Brand Scorecard" className="tabStyle">
                    <BrandScorecard
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      brandsLoading={loading}
                      chooseComparison={chooseComparison}
                      productType={productType}
                      setSelectedFilters={setSelectedFilters}
                      selectedFilters={selectedFilters}
                      isFilterByApplied={isFilterByApplied}
                      setIsFilterByApplied={setIsFilterByApplied}
                      selectedFilterValues={selectedFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                    />
                  </TabPanel>
                ) : (
                  ""
                )}
                <TabPanel header="Frequency">
                  <Frequency
                    compareBy={compareBy}
                    selectedComparitionBy={selectedComparitionBy}
                    apply={apply}
                    setApply={setApply}
                    isCompareByClicked={isCompareByClicked}
                    setIsCompareByClicked={setIsCompareByClicked}
                    chooseComparison={chooseComparison}
                    productType={productType}
                    setSelectedFilters={setSelectedFilters}
                    selectedFilters={selectedFilters}
                    isFilterByApplied={isFilterByApplied}
                    setIsFilterByApplied={setIsFilterByApplied}
                    selectedFilterValues={selectedFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    checkedCRCategories={checkedCRCategories}
                  />
                </TabPanel>
                {(deviceCode === 1 || isDualCategory) && (
                  <TabPanel header="Amount">
                    <Amount
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                      setSelectedFilters={setSelectedFilters}
                      selectedFilters={selectedFilters}
                      isFilterByApplied={isFilterByApplied}
                      setIsFilterByApplied={setIsFilterByApplied}
                      selectedFilterValues={selectedFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                    />
                  </TabPanel>
                )}
                {/* commented due to relplaced with OccassionAndNeeds and used for reference */}
                {/* {deviceCode === 2 && (
                  <TabPanel header="OCCASIONS">
                    <Occasions
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                    />
                  </TabPanel>
                )}
                {deviceCode === 2 && (
                  <TabPanel header="NEEDS">
                    <Needs
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                    />
                  </TabPanel>
                )} */}
                {(deviceCode === 2) & !isCustomRecruitCategory && (
                  <TabPanel
                    header={
                      <span>
                        XMOT<sup>&reg;</sup> Occasions & Needs
                      </span>
                    }
                  >
                    <OccasionsAndNeeds
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                      setSelectedFilters={setSelectedFilters}
                      selectedFilters={selectedFilters}
                      isFilterByApplied={isFilterByApplied}
                      setIsFilterByApplied={setIsFilterByApplied}
                      selectedFilterValues={selectedFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                    />
                  </TabPanel>
                )}
                {deviceCode === 2 && (
                  <TabPanel
                    header={
                      <span>
                        XMOT<sup>&reg;</sup>{" "}
                        {!isCustomRecruitCategory ? "Experience" : "Results"}
                      </span>
                    }
                  >
                    <Experience
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                      setSelectedFilters={setSelectedFilters}
                      selectedFilters={selectedFilters}
                      isFilterByApplied={isFilterByApplied}
                      setIsFilterByApplied={setIsFilterByApplied}
                      selectedFilterValues={selectedFilterValues}
                      setSelectedFilterValues={setSelectedFilterValues}
                    />
                  </TabPanel>
                )}
                {/* {deviceCode === 2 && (
                  <TabPanel header="LOYALTY">
                    <Loyalty />
                  </TabPanel>
                )} */}
                {/* {deviceCode === 2 && (
                  <TabPanel header="USER SUMMARY">
                    <UserSummary
                      compareBy={compareBy}
                      selectedComparitionBy={selectedComparitionBy}
                      apply={apply}
                      setApply={setApply}
                      isCompareByClicked={isCompareByClicked}
                      setIsCompareByClicked={setIsCompareByClicked}
                      chooseComparison={chooseComparison}
                      productType={productType}
                    />
                  </TabPanel>
                )} */}

                <TabPanel header="Usage Heatmap">
                  <UsageHeatmap
                    compareBy={compareBy}
                    selectedComparitionBy={selectedComparitionBy}
                    apply={apply}
                    setApply={setApply}
                    isCompareByClicked={isCompareByClicked}
                    setIsCompareByClicked={setIsCompareByClicked}
                    chooseComparison={chooseComparison}
                    productType={productType}
                    setSelectedFilters={setSelectedFilters}
                    selectedFilters={selectedFilters}
                    isFilterByApplied={isFilterByApplied}
                    setIsFilterByApplied={setIsFilterByApplied}
                    selectedFilterValues={selectedFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                  />
                </TabPanel>
              </TabView>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
